import {
  CopyOutlined,
  CreditCardOutlined,
  DownloadOutlined,
  MailOutlined,
  PayCircleOutlined,
} from '@ant-design/icons';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import { getRouteStackPath } from '@portal/config/routes';
import { translate } from '@portal/services/i18n';
import { formatCurrency } from '@portal/services/masks';
import React from 'react';
import './Invoice.less';

import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import { CardInvoice, CardTransaction } from '@portal/models/bank';
import { CardStatus, CardType } from '@portal/models/enumerators/cards';
import { getEnumDescription } from '@portal/utils/enum';
import { useParams } from 'react-router-dom';

const Invoice = () => {
  const navigate = useNavigation();
  const params: { id: string } = useParams();
  const {
    cards: { cards },
    theme: { currentTheme },
  } = useReduxState();

  const selectedCard = cards.find((card) => card.id === params.id);

  // TODO: Replace with real API integration — mock data should not be in production
  const invoiceData: CardInvoice = {
    id: '1',
    totalAmount: 0,
    minimumAmount: 0,
    availableLimit: 0,
    totalLimit: 0,
  };

  // TODO: Replace with real API integration
  const transactionMock: CardTransaction[] = [];

  const renderValues = () => {
    const data = [
      {
        label: translate('PAGES.CARDS.MY_CARDS.INVOICE.TOTAL_AMOUNT'),
        value: invoiceData.totalAmount,
      },
      {
        label: translate('PAGES.CARDS.MY_CARDS.INVOICE.MINIMUM_AMOUNT'),
        value: invoiceData.minimumAmount,
      },
      {
        label: translate('PAGES.CARDS.MY_CARDS.INVOICE.AVAILABLE_LIMIT'),
        value: invoiceData.availableLimit,
      },
      {
        label: translate('PAGES.CARDS.MY_CARDS.INVOICE.TOTAL_LIMIT'),
        value: invoiceData.totalLimit,
      },
    ];

    const values = data.map((value) => (
      <div className="invoice__bill-summary__values__item" key={value.label}>
        <p className="invoice__bill-summary__values__item__label">
          {value.label}
        </p>
        <p className="invoice__bill-summary__values__item__amount">
          {formatCurrency(value.value)}
        </p>
      </div>
    ));

    return <div className="invoice__bill-summary__values">{values}</div>;
  };

  const renderCardActions = () => {
    const actions = [
      {
        label: translate('PAGES.CARDS.MY_CARDS.INVOICE.ACTIONS.PAY'),
        icon: <CreditCardOutlined />,
        type: 'pay',
      },
      {
        label: translate(
          'PAGES.CARDS.MY_CARDS.INVOICE.ACTIONS.PAY_IN_INSTALLMENTS'
        ),
        icon: <PayCircleOutlined />,
        type: 'installment',
      },
      {
        label: translate('PAGES.CARDS.MY_CARDS.INVOICE.ACTIONS.COPY'),
        icon: <CopyOutlined />,
        type: 'copy',
      },
      {
        label: translate('PAGES.CARDS.MY_CARDS.INVOICE.ACTIONS.SENT_MAIL'),
        icon: <MailOutlined />,
        type: 'email',
      },
      {
        label: translate('PAGES.CARDS.MY_CARDS.INVOICE.ACTIONS.DOWNLOAD'),
        icon: <DownloadOutlined />,
        type: 'export',
      },
    ];

    return (
      <div className="invoice__bill-summary__actions">
        {actions.map((item) => (
          <button
            className="invoice__bill-summary__actions__item"
            key={item.type}
            disabled
            aria-disabled="true"
          >
            <span className="invoice__bill-summary__actions__item__icon">
              {item.icon}
            </span>
            <span className="invoice__bill-summary__actions__item__label">
              {item.label}
            </span>
          </button>
        ))}
      </div>
    );
  };

  const renderInvoice = () => (
    <div className="invoice__bill-summary">
      <div className="invoice__bill-summary__header">
        <div className="invoice__bill-summary__card-info">
          <div
            className="invoice__bill-summary__card-info__flag"
            style={{ background: currentTheme.background }}
          >
            {selectedCard?.tag}
          </div>
          <div className="invoice__card-details">
            <h3>
              {getEnumDescription(
                selectedCard?.type ?? CardType.PHYSICAL,
                'CARD_TYPE_NUMBER'
              )}
            </h3>
            <p>
              {translate('PAGES.CARDS.MY_CARDS.INVOICE.CARD.END')} ****{' '}
              {selectedCard?.cardNumber
                ? selectedCard.cardNumber.slice(-4)
                : '----'}
            </p>
          </div>
        </div>
        <div className="invoice__bill-summary__card-info__status">
          <div className={`invoice__bill-summary__card-info__status__badge `}>
            {selectedCard?.status === CardStatus.ACTIVE
              ? translate('COMPONENTS.ADVANCED_CARD_VIEWER.ACTIVE')
              : translate('COMPONENTS.ADVANCED_CARD_VIEWER.BLOCKED')}
          </div>
          <p className="invoice__due-date">
            {translate('PAGES.CARDS.MY_CARDS.INVOICE.CARD.MATURITY')}{' '}
            {selectedCard?.expirationDate}
          </p>
        </div>
      </div>
      {renderValues()}
      {renderCardActions()}
    </div>
  );

  const renderTransactions = () => (
    <AdvancedCard testId="invoice-transactions-card">
      <h3>{translate('PAGES.CARDS.MY_CARDS.INVOICE.TRANSACTION.TITLE')}</h3>
      <div className="invoice__transactions">
        <div className="invoice__transactions__filters">
          <button
            className="invoice__transactions__filters__buttons"
            disabled
            aria-disabled="true"
          >
            {translate('PAGES.CARDS.MY_CARDS.INVOICE.TRANSACTION.FILTERS.ALL')}
          </button>
          <button
            className="invoice__transactions__filters__buttons"
            disabled
            aria-disabled="true"
          >
            {translate(
              'PAGES.CARDS.MY_CARDS.INVOICE.TRANSACTION.FILTERS.PURCHASES'
            )}
          </button>
          <button
            className="invoice__transactions__filters__buttons"
            disabled
            aria-disabled="true"
          >
            {translate(
              'PAGES.CARDS.MY_CARDS.INVOICE.TRANSACTION.FILTERS.WITHDRAWALS'
            )}
          </button>
          <button
            className="invoice__transactions__filters__buttons"
            disabled
            aria-disabled="true"
          >
            {translate(
              'PAGES.CARDS.MY_CARDS.INVOICE.TRANSACTION.FILTERS.INSTALLMENTS'
            )}
          </button>
        </div>
        <div>
          {transactionMock.map((transaction) => (
            <div key={transaction.id} className="invoice__transactions__item">
              <div className="invoice__transactions__item__info">
                <div>
                  <h4>{transaction.description}</h4>
                  <p>
                    {transaction.dueDate} • {transaction.installments}
                  </p>
                </div>
              </div>
              <div className="invoice__transactions__item__info__amount">
                <p>{formatCurrency(transaction.amount ?? 0)}</p>
                {transaction.installmentsInfo ? (
                  <span>{transaction.installmentsInfo}</span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <div className="invoice__transactions__footer">
          <AdvancedButton
            text={translate(
              'PAGES.CARDS.MY_CARDS.INVOICE.TRANSACTION.MORE_TRANSACTIONS'
            )}
            disabled
          />
        </div>
      </div>
    </AdvancedCard>
  );

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate('COMPONENTS.BREADCRUMB.CARDS'),
            url: getRouteStackPath('CARDS', 'CARDS_PAGE'),
          },
        ]}
      />
      <div className="invoice">
        <AdvancedPanelContentHeader
          testId="invoice-header-panel"
          title={translate('PAGES.CARDS.MY_CARDS.INVOICE.TITLE')}
          description={translate('PAGES.CARDS.MY_CARDS.INVOICE.DESCRIPTION')}
          goBackAction={() =>
            navigate(getRouteStackPath('CARDS', 'MY_CARDS_PAGE'))
          }
        />
        {renderInvoice()}
        {renderTransactions()}
      </div>
    </>
  );
};

export default Invoice;
